// ============================================================================
// LT-4a — should the onboarding wizard be on screen, and what happens when it
// finishes or is skipped.
//
// ── THE RULE THAT DRIVES EVERY DECISION IN THIS FILE ────────────────────────
// "Has this company been set up?" is answered by getBusinessSettings() === null.
// The business_settings row IS the completion flag. There is no separate
// onboarded_at column, and there does not need to be one.
//
// ⛔ THEREFORE THE ROW MUST ONLY BE CREATED WHEN THE WIZARD COMPLETES.
// ONE saveBusinessSettings() call, at Finish, with the whole payload.
//
// A per-step save would look harmless and would be the worst bug in this
// package: saveBusinessSettings() UPSERTS, so step 1 would INSERT the row, and
// from that instant getBusinessSettings() is non-null. A user who typed their
// business name and then closed the tab at question 2 would never be offered
// onboarding again — on any device, ever — with a half-empty settings row and no
// way to discover what they missed. The wizard would have silently marked itself
// complete on someone who never completed it. Nothing in the UI would report it.
//
// ── COMPLETED vs SKIPPED ARE DELIBERATELY DIFFERENT ────────────────────────
//   COMPLETED -> the row exists -> server-side truth -> never shown again, on
//                any device, to anyone in the company.
//   SKIPPED   -> writes NOTHING to the database, only a localStorage flag ->
//                offered once more on another device.
// That asymmetry is correct, not a leak. A skipped company genuinely has not
// been set up; re-offering on a new device is another chance, not a nag. And it
// keeps the local flag out of the business's data, where it does not belong.
//
// ── FAIL OPEN, ALWAYS ──────────────────────────────────────────────────────
// Any error anywhere in here tears the wizard down and drops the user into the
// app WITHOUT marking onboarding done, so they can retry. A broken wizard must
// never stand between a paying customer and their own books. Tested.
// ============================================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../auth/AuthProvider";
import { getBusinessSettings, saveBusinessSettings } from "../lib/businessSettings";

/** localStorage key for a SKIPPED company. Per-company: one user skipping must
 *  not silently suppress the wizard for a different company on a shared browser
 *  (signing out and in as someone else does not reload the page — which is why
 *  clearCompanyCache() exists in db.js). */
export const skipKey = (companyId) => `m2:onboarding:v1:${companyId}`;

// ⚠️ EVERY localStorage TOUCH IS WRAPPED. In a Safari private window the
// accessor itself THROWS on read AND on write — not returns null — and an
// unguarded read here would crash the whole app shell at mount, for a
// convenience flag. Storage being unavailable must degrade to "not skipped",
// which simply means the wizard is offered. That is the safe direction.
export function readSkipped(companyId) {
  if (!companyId) return false;
  try {
    return window.localStorage.getItem(skipKey(companyId)) === "1";
  } catch {
    return false;
  }
}

export function writeSkipped(companyId) {
  if (!companyId) return false;
  try {
    window.localStorage.setItem(skipKey(companyId), "1");
    return true;
  } catch {
    // Nothing to do and nothing to report. The wizard closes either way; the
    // only cost is that it may be offered again on this browser, which is the
    // same outcome as a different device and is explicitly acceptable.
    return false;
  }
}

/**
 * Decide whether to show the wizard, and own the two ways out of it.
 *
 * @returns {{
 *   show: boolean,
 *   checking: boolean,
 *   complete: (payload: object) => Promise<void>,
 *   skip: () => void,
 *   dismiss: () => void,
 * }}
 */
export function useOnboarding() {
  const { companyId, role, billing } = useAuth();
  const [show, setShow] = useState(false);
  const [checking, setChecking] = useState(true);

  // The check is async and the company can change under it (sign out, sign in as
  // somebody else, no reload). Without this guard a slow response for company A
  // can resolve after B is on screen and show A's wizard to B.
  const activeCompany = useRef(null);

  // ⛔ THE PERMISSION GATE IS THE ONE THE APP ALREADY USES, NOT A NEW ONE.
  // Business Settings is restricted by `roles: ["owner","admin"]` on its NAV
  // entry (src/MsquaredApp.jsx:210), evaluated by itemAllowed() against
  // isFullAccess = role === "owner" || role === "admin" (:2739-2740). The same
  // two roles are exactly who can write business_settings, so this mirrors it
  // rather than inventing a parallel rule that could drift.
  //
  // A `sales` member must never see the wizard: they would fill in three screens
  // and hit 42501 at the final save, having wasted their time on a form they
  // were never allowed to submit.
  const canWriteSettings = role === "owner" || role === "admin";

  // ⚠️ THE LT-2 HALF, AND IT IS EASY TO MISS. can_write() now ALSO requires
  // company_writable, so a lapsed company answers 42501 even for its owner.
  // billing.state === 'read_only' is that state as the app already reads it
  // (src/lib/billing.js:53 — 'trialing' | 'active' | 'read_only'), and
  // MsquaredApp already renders ReadOnlyBanner on it. Showing the wizard to a
  // lapsed owner would guarantee a failed save at Finish; they should be looking
  // at the billing banner instead. A null billing (still loading, or the read
  // failed) is NOT treated as read_only — fail open.
  const billingBlocks = billing?.state === "read_only";

  useEffect(() => {
    let cancelled = false;
    activeCompany.current = companyId;

    if (!companyId || !canWriteSettings || billingBlocks) {
      setShow(false);
      setChecking(false);
      return () => { cancelled = true; };
    }

    if (readSkipped(companyId)) {
      setShow(false);
      setChecking(false);
      return () => { cancelled = true; };
    }

    setChecking(true);
    (async () => {
      let row;
      try {
        row = await getBusinessSettings();
      } catch {
        // FAIL OPEN. We cannot tell whether this company is set up, so we do not
        // guess. Not showing the wizard costs a new company a nicety; showing it
        // to an established company would put a modal over their books over a
        // transient read error. The quiet option is the right one here.
        if (!cancelled) { setShow(false); setChecking(false); }
        return;
      }
      if (cancelled || activeCompany.current !== companyId) return;
      setShow(row === null);
      setChecking(false);
    })();

    return () => { cancelled = true; };
  }, [companyId, canWriteSettings, billingBlocks]);

  /**
   * Finish. THE ONLY WRITE IN THE WHOLE PACKAGE.
   *
   * Throws on failure so the caller can decide what the user sees — this hook
   * does NOT swallow it and does NOT hide the wizard on error, because hiding it
   * would look exactly like success to the person watching.
   */
  const complete = useCallback(async (payload) => {
    // ⛔ company_id IS NEVER SENT. A trigger sets it from the caller's
    // membership; supplying it is at best ignored and at worst confusing to read.
    await saveBusinessSettings(payload);
    // ⛔ DELIBERATELY DOES NOT setShow(false) — AND A TEST CAUGHT THIS.
    // The first version hid the wizard here, which unmounted it the instant the
    // save resolved, so the "You're set up." screen never rendered at all: the
    // user's reward for finishing was the app blinking into existence with no
    // confirmation that anything had been saved. The wizard advances itself to
    // its own done screen and stays mounted until the user picks one of the two
    // exits, which call dismiss(). Completion is already durable at this point —
    // the row exists, so a reload cannot re-show the wizard either way.
  }, []);

  /** Skip. Writes NOTHING to the database — the company is still not set up. */
  const skip = useCallback(() => {
    writeSkipped(companyId);
    setShow(false);
  }, [companyId]);

  /**
   * Tear the wizard down WITHOUT recording anything, anywhere.
   *
   * This is the fail-open exit. It deliberately does NOT write the skip flag:
   * a wizard that crashed must be offered again, and a company that hit a save
   * error has not been set up. Marking it done here would convert one bad
   * request into a permanent loss of the whole flow.
   */
  const dismiss = useCallback(() => setShow(false), []);

  return { show, checking, complete, skip, dismiss };
}
